import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faEnvelope, 
  faLock, 
  faCamera, 
  faUserFriends, 
  faCalendarAlt, 
  faBirthdayCake, 
  faFireFlameSimple,
  faSave, 
  faSignOutAlt, 
  faTimes, 
  faEdit, 
  faPlus, 
  faTrash, 
  faCheck,
  faCommentDots,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import profileService from '../api/profileService';
import authService from '../api/authService';
import '../styles/MemberProfile.css';

const MemberProfilePage = () => {
  const { currentUser, logout, refreshAuthToken } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State for profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    bio: '',
    familyStatus: '',
    anniversary: '',
    kidsBirthdays: [],
    yartzeits: []
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  
  // Privacy notice state
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(true);
  
  // Form fields states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [emailData, setEmailData] = useState({
    newEmail: '',
    password: ''
  });
  
  // States for editing sections
  const [activeEdits, setActiveEdits] = useState({
    personalInfo: false,
    anniversary: false,
    email: false,
    password: false
  });
  
  // State for handling new/edited kid data
  const [editingKid, setEditingKid] = useState(null);
  const [newKidData, setNewKidData] = useState({
    id: null,
    name: '',
    birthdate: ''
  });
  
  // State for handling new/edited yartzeit data
  const [editingYartzeit, setEditingYartzeit] = useState(null);
  const [newYartzeitData, setNewYartzeitData] = useState({
    id: null,
    relationship: '',
    date: ''
  });
  
  // Available relationship options for the dropdown
  const relationshipOptions = [
    'Father', 'Mother', 'Grandfather', 'Grandmother', 
    'Brother', 'Sister', 'Spouse', 'Child', 'Other'
  ];
  
  // Family status options
  const familyStatusOptions = [
    { id: 'single', title: 'Single' },
    { id: 'married-kids', title: 'Married - with kids' },
    { id: 'married-no-kids', title: 'Married - no kids' },
    { id: 'other', title: 'Other' }
  ];

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfileData = async () => {
      // 🔧 NEW: Enhanced loading with retry logic
      let attempt = 0;
      const maxRetries = 3;
      const retryDelays = [1000, 2000, 3000];

      const attemptFetch = async (retryAttempt = 0) => {
        try {
          setLoading(true);
          setError('');
          
          // 🚨 CRITICAL SECURITY FIX: Clear all component state before fetching new user data
          // This prevents user A's cached state from being used for user B
          console.log('🔒 SECURITY: Clearing all profile component state before fetching new data');
          setProfileData({
            name: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            bio: '',
            familyStatus: '',
            anniversary: '',
            kidsBirthdays: [],
            yartzeits: []
          });
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setEmailData({
            newEmail: '',
            password: ''
          });
          setActiveEdits({
            personalInfo: false,
            anniversary: false,
            email: false,
            password: false
          });
          setAvatarFile(null);
          setAvatarPreview(null);

          // 🔧 Session Recovery: Try to refresh auth token if this is a retry
          if (retryAttempt > 0) {
            console.log('🔄 Attempting session recovery for profile...');
            try {
              await refreshAuthToken();
              console.log('✅ Session recovery successful for profile');
            } catch (recoveryError) {
              console.warn('⚠️ Session recovery failed for profile:', recoveryError);
            }
          }

          // Fetch with timeout
          const response = await Promise.race([
            profileService.getProfile(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Profile request timeout')), 10000)
            )
          ]);
          
          if (response && response.data) {
            const userData = response.data;
            
            // 🔄 SIMPLIFIED APPROACH: Trust the server's profile response
            // Remove overly strict validation that was causing logout loops
            console.log('🔒 SECURITY: Setting profile data for user:', userData.email);
            
            // Parse JSON strings if needed
            const kidsBirthdays = typeof userData.kids_birthdays === 'string' 
              ? JSON.parse(userData.kids_birthdays || '[]') 
              : (userData.kids_birthdays || []);
              
            const yartzeits = typeof userData.yartzeits === 'string' 
              ? JSON.parse(userData.yartzeits || '[]') 
              : (userData.yartzeits || []);
            
            setProfileData({
              name: userData.name || '',
              email: userData.email || '',
              phone: userData.phone || '',
              address: userData.address || '',
              city: userData.city || '',
              state: userData.state || '',
              zipCode: userData.zip_code || '',
              bio: userData.bio || '',
              familyStatus: userData.family_status || '',
              anniversary: userData.anniversary || '',
              kidsBirthdays,
              yartzeits
            });
            
            // Set avatar preview if available
            if (userData.profile_image) {
              setAvatarPreview(userData.profile_image);
            }
            
            // Initialize email form
            setEmailData(prev => ({
              ...prev,
              newEmail: userData.email || ''
            }));

            console.log('✅ Profile data loaded successfully');
          }
        } catch (error) {
          console.error(`❌ Error fetching profile data (attempt ${retryAttempt + 1}):`, error);
          
          if (retryAttempt < maxRetries) {
            console.log(`🔄 Retrying profile fetch in ${retryDelays[retryAttempt]}ms...`);
            setTimeout(() => {
              attemptFetch(retryAttempt + 1);
            }, retryDelays[retryAttempt]);
            return; // Don't set loading to false yet
          } else {
            console.error('❌ All profile fetch attempts exhausted');
            setError('Failed to load profile data. Please try refreshing the page or contact support if the problem persists.');
          }
        } finally {
          if (retryAttempt >= maxRetries || retryAttempt === 0) {
            setLoading(false);
          }
        }
      };

      await attemptFetch(0);
    };
    
    // 🔒 SECURITY: Only fetch profile if we have a current user
    if (currentUser) {
      fetchProfileData();
    } else {
      // If no current user, clear all state
      setLoading(false);
    }
  }, [currentUser, refreshAuthToken]); // 🔒 CRITICAL: Re-run when currentUser changes

  // Check if privacy notice should be shown
  useEffect(() => {
    const hidePrivacyNotice = localStorage.getItem('hideProfilePrivacyNotice');
    if (hidePrivacyNotice === 'true') {
      setShowPrivacyNotice(false);
    }
  }, []);

  // Handle hiding privacy notice permanently
  const handleHidePrivacyNotice = () => {
    localStorage.setItem('hideProfilePrivacyNotice', 'true');
    setShowPrivacyNotice(false);
  };

  // Handle avatar file selection
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, or GIF)');
        return;
      }
      
      if (file.size > maxSize) {
        setError('Image file is too large. Please select a file smaller than 5MB.');
        return;
      }
      
      setAvatarFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload the avatar immediately
      uploadAvatar(file);
    }
  };
  
  // Handle avatar upload
  const uploadAvatar = async (file) => {
    try {
      setSaving(true);
      setError('');
      
      const response = await profileService.uploadProfileImage(file);
      
      if (response && response.data && response.data.imageUrl) {
        setAvatarPreview(response.data.imageUrl);
        setSuccessMessage('Profile picture updated successfully!');
        
        // Update the current user in localStorage with the new avatar
        const user = authService.getCurrentUser();
        if (user) {
          user.profile_image = response.data.imageUrl;
          localStorage.setItem('user', JSON.stringify(user));
        }
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setError('Failed to upload profile image. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle edit mode for a section
  const toggleEditMode = (section) => {
    setActiveEdits(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    
    // Reset any error/success message when toggling edit mode
    setError('');
    setSuccessMessage('');
  };
  
  // Handle input change for profile data
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle input change for password form
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle input change for email form
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle saving personal information
  const handleSavePersonalInfo = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Split the full name into first and last name
      const nameParts = profileData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const personalData = {
        firstName,
        lastName,
        phone: profileData.phone,
        address: profileData.address,
        city: profileData.city,
        state: profileData.state,
        zipCode: profileData.zipCode,
        bio: profileData.bio
      };
      
      await profileService.updateProfile(personalData);
      
      // Update user in localStorage if name changed
      const user = authService.getCurrentUser();
      if (user && user.name !== profileData.name) {
        user.name = profileData.name;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      setSuccessMessage('Personal information updated successfully!');
      toggleEditMode('personalInfo');
    } catch (error) {
      console.error('Error updating personal information:', error);
      setError('Failed to update personal information. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle saving family status
  const handleSaveFamilyStatus = async () => {
    try {
      setSaving(true);
      setError('');
      
      await profileService.updateProfile({
        familyStatus: profileData.familyStatus
      });
      
      setSuccessMessage('Family status updated successfully!');
    } catch (error) {
      console.error('Error updating family status:', error);
      setError('Failed to update family status. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle saving anniversary date
  const handleSaveAnniversary = async () => {
    try {
      setSaving(true);
      setError('');
      
      await profileService.updateProfile({
        anniversary: profileData.anniversary
      });
      
      setSuccessMessage('Anniversary date updated successfully!');
      toggleEditMode('anniversary');
    } catch (error) {
      console.error('Error updating anniversary date:', error);
      setError('Failed to update anniversary date. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle updating email
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!emailData.newEmail || !emailData.password) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      await profileService.updateEmail({
        email: emailData.newEmail,
        password: emailData.password
      });
      
      // Update user data with new email
      const user = authService.getCurrentUser();
      if (user) {
        user.email = emailData.newEmail;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      // Update profile data
      setProfileData(prev => ({
        ...prev,
        email: emailData.newEmail
      }));
      
      setSuccessMessage('Email updated successfully!');
      toggleEditMode('email');
      
      // Reset password field
      setEmailData(prev => ({
        ...prev,
        password: ''
      }));
    } catch (error) {
      console.error('Error updating email:', error);
      setError(error.response?.data?.message || 'Failed to update email. Please verify your password and try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle updating password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    // Password validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      await authService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setSuccessMessage('Password updated successfully!');
      toggleEditMode('password');
      
      // Reset password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error updating password:', error);
      setError(error.response?.data?.message || 'Failed to update password. Please verify your current password and try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle input change for kid data
  const handleKidChange = (e) => {
    const { name, value } = e.target;
    setNewKidData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Start editing a kid or adding a new one
  const handleEditKid = (kid) => {
    if (kid) {
      setEditingKid(kid.id);
      setNewKidData({
        id: kid.id,
        name: kid.name,
        birthdate: kid.birthdate
      });
    } else {
      // Adding a new kid
      const newId = profileData.kidsBirthdays.length > 0 
        ? Math.max(...profileData.kidsBirthdays.map(k => k.id)) + 1 
        : 1;
        
      setEditingKid('new');
      setNewKidData({
        id: newId,
        name: '',
        birthdate: ''
      });
    }
  };
  
  // Cancel editing kid
  const handleCancelEditKid = () => {
    setEditingKid(null);
    setNewKidData({
      id: null,
      name: '',
      birthdate: ''
    });
  };
  
  // Save kid data
  const handleSaveKid = async () => {
    // Validate fields
    if (!newKidData.name || !newKidData.birthdate) {
      setError('Please fill in all required fields for the child');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      let updatedKids;
      
      if (editingKid === 'new') {
        // Add new kid
        updatedKids = [...profileData.kidsBirthdays, newKidData];
      } else {
        // Update existing kid
        updatedKids = profileData.kidsBirthdays.map(kid => 
          kid.id === editingKid ? newKidData : kid
        );
      }
      
      // Update profile with new kids data
      await profileService.updateProfile({
        kidsBirthdays: updatedKids
      });
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        kidsBirthdays: updatedKids
      }));
      
      setSuccessMessage(editingKid === 'new' ? 'Child added successfully!' : 'Child information updated successfully!');
      handleCancelEditKid();
    } catch (error) {
      console.error('Error updating child information:', error);
      setError('Failed to update child information. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Remove a kid
  const handleRemoveKid = async (kidId) => {
    try {
      setSaving(true);
      setError('');
      
      const updatedKids = profileData.kidsBirthdays.filter(kid => kid.id !== kidId);
      
      // Update profile with new kids data
      await profileService.updateProfile({
        kidsBirthdays: updatedKids
      });
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        kidsBirthdays: updatedKids
      }));
      
      setSuccessMessage('Child removed successfully!');
    } catch (error) {
      console.error('Error removing child:', error);
      setError('Failed to remove child. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle input change for yartzeit data
  const handleYartzeitChange = (e) => {
    const { name, value } = e.target;
    setNewYartzeitData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Start editing a yartzeit or adding a new one
  const handleEditYartzeit = (yartzeit) => {
    if (yartzeit) {
      setEditingYartzeit(yartzeit.id);
      setNewYartzeitData({
        id: yartzeit.id,
        relationship: yartzeit.relationship,
        date: yartzeit.date
      });
    } else {
      // Adding a new yartzeit
      const newId = profileData.yartzeits.length > 0 
        ? Math.max(...profileData.yartzeits.map(y => y.id)) + 1 
        : 1;
        
      setEditingYartzeit('new');
      setNewYartzeitData({
        id: newId,
        relationship: '',
        date: ''
      });
    }
  };
  
  // Cancel editing yartzeit
  const handleCancelEditYartzeit = () => {
    setEditingYartzeit(null);
    setNewYartzeitData({
      id: null,
      relationship: '',
      date: ''
    });
  };
  
  // Save yartzeit data
  const handleSaveYartzeit = async () => {
    // Validate fields
    if (!newYartzeitData.relationship || !newYartzeitData.date) {
      setError('Please fill in all required fields for the yartzeit');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      let updatedYartzeits;
      
      if (editingYartzeit === 'new') {
        // Add new yartzeit
        updatedYartzeits = [...profileData.yartzeits, newYartzeitData];
      } else {
        // Update existing yartzeit
        updatedYartzeits = profileData.yartzeits.map(yartzeit => 
          yartzeit.id === editingYartzeit ? newYartzeitData : yartzeit
        );
      }
      
      // Update profile with new yartzeits data
      await profileService.updateProfile({
        yartzeits: updatedYartzeits
      });
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        yartzeits: updatedYartzeits
      }));
      
      setSuccessMessage(editingYartzeit === 'new' ? 'Yartzeit added successfully!' : 'Yartzeit information updated successfully!');
      handleCancelEditYartzeit();
    } catch (error) {
      console.error('Error updating yartzeit information:', error);
      setError('Failed to update yartzeit information. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Remove a yartzeit
  const handleRemoveYartzeit = async (yartzeitId) => {
    try {
      setSaving(true);
      setError('');
      
      const updatedYartzeits = profileData.yartzeits.filter(yartzeit => yartzeit.id !== yartzeitId);
      
      // Update profile with new yartzeits data
      await profileService.updateProfile({
        yartzeits: updatedYartzeits
      });
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        yartzeits: updatedYartzeits
      }));
      
      setSuccessMessage('Yartzeit removed successfully!');
    } catch (error) {
      console.error('Error removing yartzeit:', error);
      setError('Failed to remove yartzeit. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Format date for display - Fixed to handle timezone offset issues
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Parse the date string as local date to avoid timezone offset issues
      // Split the date string (YYYY-MM-DD) and create date with local timezone
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2], 10);
        const date = new Date(year, month, day);
        
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } else {
        // Fallback for other date formats
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (e) {
      return dateString;
    }
  };
  
  // Handle delete account modal
  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteConfirmEmail('');
    setDeleteReason('');
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmEmail('');
    setDeleteReason('');
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      setError('');

      // Validate email confirmation
      if (deleteConfirmEmail !== profileData.email) {
        setError('Email confirmation does not match your account email');
        return;
      }

      const deleteData = {
        confirmEmail: deleteConfirmEmail,
        reason: deleteReason || 'User requested account deletion'
      };

      await profileService.deactivateAccount(deleteData);
      
      setSuccessMessage('Account deactivated successfully. You will be logged out shortly.');
      
      // Log out the user after a short delay
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);

    } catch (error) {
      console.error('Error deleting account:', error);
      setError(error.response?.data?.message || 'Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Loading overlay during save operations */}
      {saving && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar-container">
          <div className="profile-avatar">
            {avatarPreview ? (
              <img src={avatarPreview} alt={profileData.name} />
            ) : (
              <div className="profile-avatar-placeholder">
                {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
          </div>
          
          <label htmlFor="avatar-input" className="change-avatar-button">
            <FontAwesomeIcon icon={faCamera} />
          </label>
          
          <input
            type="file"
            id="avatar-input"
            ref={fileInputRef}
            onChange={handleAvatarChange}
            accept="image/*"
            className="avatar-input"
          />
        </div>
        
        <h1>{profileData.name || 'Your Profile'}</h1>
        <p>{profileData.email}</p>
      </div>
      
      {/* Error and Success Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {/* Privacy Notice */}
      {showPrivacyNotice && (
        <div className="privacy-notice">
          <div className="privacy-notice-content">
            <h2>🔒 Your Privacy Matters</h2>
            <p>
              Your profile information is only visible to you and your community leader. 
              This helps your congregation leaders stay aware of important dates like anniversaries, 
              birthdays, and yartzeits to better support your family.
            </p>
            <div className="privacy-checkbox-container">
              <input 
                type="checkbox" 
                id="dontShowAgain" 
                onChange={(e) => {
                  if (e.target.checked) {
                    handleHidePrivacyNotice();
                  }
                }}
              />
              <label htmlFor="dontShowAgain">Don't show this again</label>
            </div>
            <div className="button-group">
              <button 
                className="dismiss-button" 
                onClick={() => setShowPrivacyNotice(false)}
              >
                <FontAwesomeIcon icon={faTimes} /> Got it
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Content - All sections in a single scrollable view */}
      <div className="profile-form">
        {/* Personal Information Section */}
        <div className="form-section">
          <div className="form-section-header">
            <FontAwesomeIcon icon={faUser} />
            <span>Personal Information</span>
          </div>
          
          {activeEdits.personalInfo ? (
            <div className="editable-form">
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={profileData.name} 
                  onChange={handleProfileChange} 
                  placeholder="Your full name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={profileData.phone} 
                  onChange={handleProfileChange} 
                  placeholder="Your phone number"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input 
                  type="text" 
                  id="address" 
                  name="address" 
                  value={profileData.address} 
                  onChange={handleProfileChange} 
                  placeholder="Your street address"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input 
                    type="text" 
                    id="city" 
                    name="city" 
                    value={profileData.city} 
                    onChange={handleProfileChange} 
                    placeholder="City"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input 
                    type="text" 
                    id="state" 
                    name="state" 
                    value={profileData.state} 
                    onChange={handleProfileChange} 
                    placeholder="State"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="zipCode">Zip Code</label>
                  <input 
                    type="text" 
                    id="zipCode" 
                    name="zipCode" 
                    value={profileData.zipCode} 
                    onChange={handleProfileChange} 
                    placeholder="Zip code"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="bio">About You</label>
                <textarea 
                  id="bio" 
                  name="bio" 
                  value={profileData.bio} 
                  onChange={handleProfileChange} 
                  placeholder="Tell us a bit about yourself"
                ></textarea>
              </div>
              
              <div className="button-group">
                <button 
                  className="cancel-button" 
                  onClick={() => toggleEditMode('personalInfo')}
                >
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button 
                  className="save-button" 
                  onClick={handleSavePersonalInfo}
                >
                  <FontAwesomeIcon icon={faSave} /> Save
                </button>
              </div>
            </div>
          ) : (
            <div className="view-form">
              <div className="editable-item">
                <div className="editable-item-header">
                  <h3>Full Name</h3>
                  <button 
                    className="edit-button" 
                    onClick={() => toggleEditMode('personalInfo')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                </div>
                <div className="item-value">{profileData.name || 'Not provided'}</div>
              </div>
              
              <div className="editable-item">
                <div className="editable-item-header">
                  <h3>Contact Information</h3>
                </div>
                <div className="item-value">
                  <p>Phone: {profileData.phone || 'Not provided'}</p>
                  <p>Address: {profileData.address ? (
                    `${profileData.address}, ${profileData.city}, ${profileData.state} ${profileData.zipCode}`
                  ) : 'Not provided'}</p>
                </div>
              </div>
              
              <div className="editable-item">
                <div className="editable-item-header">
                  <h3>About</h3>
                </div>
                <div className="item-value">{profileData.bio || 'No bio provided'}</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Family Status Section */}
        <div className="form-section">
          <div className="form-section-header">
            <FontAwesomeIcon icon={faUserFriends} />
            <span>Family Status</span>
          </div>
          
          <div className="form-group">
            <label htmlFor="familyStatus">Family Status</label>
            <select 
              id="familyStatus" 
              name="familyStatus" 
              value={profileData.familyStatus} 
              onChange={handleProfileChange}
            >
              <option value="">Select your family status</option>
              {familyStatusOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.title}
                </option>
              ))}
            </select>
            
            <div className="button-group">
              <button 
                className="save-button" 
                onClick={handleSaveFamilyStatus}
                disabled={!profileData.familyStatus}
              >
                <FontAwesomeIcon icon={faSave} /> Save
              </button>
            </div>
          </div>
        </div>
        
        {/* Anniversary Section - Only for married statuses */}
        {(profileData.familyStatus === 'married-kids' || profileData.familyStatus === 'married-no-kids') && (
          <div className="form-section">
            <div className="form-section-header">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>Anniversary</span>
            </div>
            
            {activeEdits.anniversary ? (
              <div className="editable-form">
                <div className="form-group">
                  <label htmlFor="anniversary">Anniversary Date</label>
                  <input 
                    type="date" 
                    id="anniversary" 
                    name="anniversary" 
                    value={profileData.anniversary} 
                    onChange={handleProfileChange}
                    max={new Date().toISOString().split('T')[0]} // Prevent future dates
                  />
                </div>
                
                <div className="button-group">
                  <button 
                    className="cancel-button" 
                    onClick={() => toggleEditMode('anniversary')}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                  <button 
                    className="save-button" 
                    onClick={handleSaveAnniversary}
                  >
                    <FontAwesomeIcon icon={faSave} /> Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="view-form">
                <div className="editable-item">
                  <div className="editable-item-header">
                    <h3>Anniversary Date</h3>
                    <button 
                      className="edit-button" 
                      onClick={() => toggleEditMode('anniversary')}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                  </div>
                  <div className="item-value">
                    {profileData.anniversary 
                      ? formatDateForDisplay(profileData.anniversary) 
                      : 'Not provided'}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Kids Section - Only for married with kids status */}
        {profileData.familyStatus === 'married-kids' && (
          <div className="form-section">
            <div className="form-section-header">
              <FontAwesomeIcon icon={faBirthdayCake} />
              <span>Children</span>
            </div>
            
            {/* List of kids */}
            {profileData.kidsBirthdays.length > 0 ? (
              <ul className="kids-list">
                {profileData.kidsBirthdays.map(kid => (
                  <li key={kid.id} className="kid-item">
                    {editingKid === kid.id ? (
                      <div className="edit-kid-form">
                        <div className="form-group">
                          <label htmlFor="kidName">Name</label>
                          <input 
                            type="text" 
                            id="kidName" 
                            name="name" 
                            value={newKidData.name} 
                            onChange={handleKidChange} 
                            placeholder="Child's name"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="kidBirthdate">Birthdate</label>
                          <input 
                            type="date" 
                            id="kidBirthdate" 
                            name="birthdate" 
                            value={newKidData.birthdate} 
                            onChange={handleKidChange}
                            max={new Date().toISOString().split('T')[0]} // Prevent future dates
                          />
                        </div>
                        
                        <div className="button-group">
                          <button 
                            className="save-button" 
                            onClick={handleSaveKid}
                          >
                            <FontAwesomeIcon icon={faSave} /> Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="kid-item-details">
                          <span className="kid-name">{kid.name}</span>
                          <span className="kid-birthdate">
                            {formatDateForDisplay(kid.birthdate)}
                          </span>
                        </div>
                        <button 
                          className="edit-button" 
                          onClick={() => handleEditKid(kid)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button 
                          className="remove-button" 
                          onClick={() => handleRemoveKid(kid.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="item-value">No children added yet.</p>
            )}
            
            {/* Add new kid form */}
            {editingKid === 'new' ? (
              <div className="add-kid-form">
                <div className="form-group">
                  <label htmlFor="newKidName">Name</label>
                  <input 
                    type="text" 
                    id="newKidName" 
                    name="name" 
                    value={newKidData.name} 
                    onChange={handleKidChange} 
                    placeholder="Child's name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="newKidBirthdate">Birthdate</label>
                  <input 
                    type="date" 
                    id="newKidBirthdate" 
                    name="birthdate" 
                    value={newKidData.birthdate} 
                    onChange={handleKidChange}
                    max={new Date().toISOString().split('T')[0]} // Prevent future dates
                  />
                </div>
                
                <div className="button-group">
                  <button 
                    className="save-button" 
                    onClick={handleSaveKid}
                  >
                    <FontAwesomeIcon icon={faSave} /> Save
                  </button>
                </div>
              </div>
            ) : (
              <button 
                className="add-new-button" 
                onClick={() => handleEditKid(null)}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Child
              </button>
            )}
          </div>
        )}
        
        {/* Yartzeits Section */}
        <div className="form-section">
          <div className="form-section-header">
            <FontAwesomeIcon icon={faFireFlameSimple} />
            <span>Yartzeits</span>
          </div>
          
          {/* List of yartzeits */}
          {profileData.yartzeits.length > 0 ? (
            <ul className="yartzeit-list">
              {profileData.yartzeits.map(yartzeit => (
                <li key={yartzeit.id} className="yartzeit-item">
                  {editingYartzeit === yartzeit.id ? (
                    <div className="edit-yartzeit-form">
                      <div className="form-group">
                        <label htmlFor="yartzeitRelationship">Relationship</label>
                        <select 
                          id="yartzeitRelationship" 
                          name="relationship" 
                          value={newYartzeitData.relationship} 
                          onChange={handleYartzeitChange}
                        >
                          <option value="">Select relationship</option>
                          {relationshipOptions.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="yartzeitDate">Date</label>
                        <input 
                          type="date" 
                          id="yartzeitDate" 
                          name="date" 
                          value={newYartzeitData.date} 
                          onChange={handleYartzeitChange}
                          max={new Date().toISOString().split('T')[0]} // Prevent future dates
                        />
                      </div>
                      
                      <div className="button-group">
                        <button 
                          className="save-button" 
                          onClick={handleSaveYartzeit}
                        >
                          <FontAwesomeIcon icon={faSave} /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="yartzeit-item-details">
                        <span className="yartzeit-relation">{yartzeit.relationship}</span>
                        <span className="yartzeit-date">
                          {formatDateForDisplay(yartzeit.date)}
                        </span>
                      </div>
                      <button 
                        className="edit-button" 
                        onClick={() => handleEditYartzeit(yartzeit)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="remove-button" 
                        onClick={() => handleRemoveYartzeit(yartzeit.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="item-value">No yartzeits added yet.</p>
          )}
          
          {/* Add new yartzeit form */}
          {editingYartzeit === 'new' ? (
            <div className="add-yartzeit-form">
              <div className="form-group">
                <label htmlFor="newYartzeitRelationship">Relationship</label>
                <select 
                  id="newYartzeitRelationship" 
                  name="relationship" 
                  value={newYartzeitData.relationship} 
                  onChange={handleYartzeitChange}
                >
                  <option value="">Select relationship</option>
                  {relationshipOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="newYartzeitDate">Date</label>
                <input 
                  type="date" 
                  id="newYartzeitDate" 
                  name="date" 
                  value={newYartzeitData.date} 
                  onChange={handleYartzeitChange}
                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                />
              </div>
              
              <div className="button-group">
                <button 
                  className="save-button" 
                  onClick={handleSaveYartzeit}
                >
                  <FontAwesomeIcon icon={faSave} /> Save
                </button>
              </div>
            </div>
          ) : (
            <button 
              className="add-new-button" 
              onClick={() => handleEditYartzeit(null)}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Yartzeit
            </button>
          )}
        </div>
        
        {/* Email Section */}
        <div className="form-section">
          <div className="form-section-header">
            <FontAwesomeIcon icon={faEnvelope} />
            <span>Email Address</span>
          </div>
          
          {activeEdits.email ? (
            <form onSubmit={handleUpdateEmail} className="editable-form">
              <div className="form-group">
                <label htmlFor="newEmail">New Email Address</label>
                <input 
                  type="email" 
                  id="newEmail" 
                  name="newEmail" 
                  value={emailData.newEmail} 
                  onChange={handleEmailChange} 
                  placeholder="Enter new email address"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="emailPassword">Current Password (for verification)</label>
                <input 
                  type="password" 
                  id="emailPassword" 
                  name="password" 
                  value={emailData.password} 
                  onChange={handleEmailChange} 
                  placeholder="Enter your current password"
                  required
                />
              </div>
              
              <div className="button-group">
                <button 
                  type="button"
                  className="cancel-button" 
                  onClick={() => toggleEditMode('email')}
                >
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                >
                  <FontAwesomeIcon icon={faSave} /> Update Email
                </button>
              </div>
            </form>
          ) : (
            <div className="view-form">
              <div className="editable-item">
                <div className="editable-item-header">
                  <h3>Email Address</h3>
                  <button 
                    className="edit-button" 
                    onClick={() => toggleEditMode('email')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Change
                  </button>
                </div>
                <div className="item-value">{profileData.email}</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Password Section */}
        <div className="form-section">
          <div className="form-section-header">
            <FontAwesomeIcon icon={faLock} />
            <span>Password</span>
          </div>
          
          {activeEdits.password ? (
            <form onSubmit={handleUpdatePassword} className="editable-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input 
                  type="password" 
                  id="currentPassword" 
                  name="currentPassword" 
                  value={passwordData.currentPassword} 
                  onChange={handlePasswordChange} 
                  placeholder="Enter current password"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input 
                  type="password" 
                  id="newPassword" 
                  name="newPassword" 
                  value={passwordData.newPassword} 
                  onChange={handlePasswordChange} 
                  placeholder="Enter new password"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input 
                  type="password" 
                  id="confirmPassword" 
                  name="confirmPassword" 
                  value={passwordData.confirmPassword} 
                  onChange={handlePasswordChange} 
                  placeholder="Confirm new password"
                  required
                />
              </div>
              
              <div className="button-group">
                <button 
                  type="button"
                  className="cancel-button" 
                  onClick={() => toggleEditMode('password')}
                >
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                >
                  <FontAwesomeIcon icon={faSave} /> Update Password
                </button>
              </div>
            </form>
          ) : (
            <div className="view-form">
              <div className="editable-item">
                <div className="editable-item-header">
                  <h3>Password</h3>
                  <button 
                    className="edit-button" 
                    onClick={() => toggleEditMode('password')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Change
                  </button>
                </div>
                <div className="item-value">••••••••</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Support Section */}
        <div className="form-section">
          <div className="form-section-header">
            <FontAwesomeIcon icon={faCommentDots} />
            <span>Support</span>
          </div>
          
          <div className="support-content">
            <p>Experiencing issues or have feedback or ideas? We would love to hear from you.</p>
            <a 
              href="https://wa.me/message/CUTHC4T6KYIOL1" 
              className="whatsapp-support-link"
              target="_blank" 
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faCommentDots} /> Contact Support on WhatsApp
            </a>
          </div>
        </div>
        
        {/* Account Actions Section */}
        <div className="account-section">
          <h2>Account Actions</h2>
          <div className="account-actions">
            <button 
              className="logout-button" 
              onClick={handleLogout}
            >
              <FontAwesomeIcon icon={faSignOutAlt} /> Log Out
            </button>
            
            <div className="delete-account-container">
              <span 
                className="delete-account-link" 
                onClick={handleShowDeleteModal}
              >
                Delete Account
              </span>
            </div>
          </div>
        </div>

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="modal delete-account-modal">
              <div className="modal-header">
                <h3>
                  <FontAwesomeIcon icon={faExclamationTriangle} /> 
                  Delete Account
                </h3>
                <button 
                  className="modal-close" 
                  onClick={handleCloseDeleteModal}
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className="modal-content">
                <div className="simple-warning">
                  <p>This action will permanently delete your account and remove all your data.</p>
                  <p>You will be logged out immediately and will no longer have access to your account.</p>
                  <p><strong>This action cannot be undone.</strong></p>
                </div>

                <div className="form-group">
                  <label htmlFor="deleteConfirmEmail">
                    Confirm your email address to proceed:
                  </label>
                  <input
                    type="email"
                    id="deleteConfirmEmail"
                    value={deleteConfirmEmail}
                    onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                    placeholder="Enter your email address"
                    disabled={isDeleting}
                  />
                </div>

                <div className="modal-actions">
                  <button 
                    className="cancel-button" 
                    onClick={handleCloseDeleteModal}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button 
                    className="delete-confirm-button" 
                    onClick={handleDeleteAccount}
                    disabled={isDeleting || !deleteConfirmEmail}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberProfilePage;